import React, { useState, useEffect, useCallback } from 'react';
import { FaXmark } from 'react-icons/fa6';
import * as adminFrameworksService from '../../data/services/adminFrameworksService';
import './PromptSelector.css';

const PromptSelector = ({ onSelect, onCancel, existingPromptIds = [], roleId = null }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPromptIds, setSelectedPromptIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      // If roleId provided, fetch prompts available for that role (global + org-specific if role has orgId)
      // Otherwise, fetch only global prompts
      const data = roleId 
        ? await adminFrameworksService.getAvailablePromptsForRole(roleId)
        : await adminFrameworksService.getAllGlobalPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('Error loading prompts:', error);
      alert('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const handleTogglePrompt = (promptId) => {
    setSelectedPromptIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  const handleSelect = () => {
    const selectedPrompts = prompts.filter(p => selectedPromptIds.has(p.promptId || p.id));
    onSelect(selectedPrompts);
  };

  const filteredPrompts = prompts.filter(p => {
    if (existingPromptIds.includes(p.promptId || p.id)) {
      return false; // Exclude already assigned prompts
    }
    if (!searchTerm) return true;
    const title = (p.title || '').toLowerCase();
    const description = (p.description || '').toLowerCase();
    return title.includes(searchTerm.toLowerCase()) || description.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="prompt-selector-overlay" onClick={onCancel}>
      <div className="prompt-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="prompt-selector-header">
          <h2 className="prompt-selector-title">Select Prompts</h2>
          <button className="close-button" onClick={onCancel}>
            <FaXmark className="close-icon" />
          </button>
        </div>

        <div className="prompt-selector-search">
          <input
            type="text"
            className="search-input"
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading-prompts">Loading prompts...</div>
        ) : (
          <>
            <div className="prompt-selector-list">
              {filteredPrompts.length > 0 ? (
                filteredPrompts.map((prompt) => {
                  const promptId = prompt.promptId || prompt.id;
                  const isSelected = selectedPromptIds.has(promptId);
                  return (
                    <div
                      key={promptId}
                      className={`prompt-selector-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleTogglePrompt(promptId)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleTogglePrompt(promptId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="prompt-selector-item-content">
                        <div className="prompt-selector-item-title">
                          {prompt.title || 'Untitled'}
                          {prompt.isGlobal && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
                              (Global)
                            </span>
                          )}
                          {!prompt.isGlobal && prompt.orgDomain && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
                              ({prompt.orgDomain})
                            </span>
                          )}
                        </div>
                        {prompt.description && (
                          <div className="prompt-selector-item-description">{prompt.description}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-prompts">
                  {searchTerm ? 'No prompts found matching your search' : 'No prompts available'}
                </div>
              )}
            </div>

            <div className="prompt-selector-actions">
              <button className="cancel-button" onClick={onCancel}>
                Cancel
              </button>
              <button
                className="select-button"
                onClick={handleSelect}
                disabled={selectedPromptIds.size === 0}
              >
                Add Selected ({selectedPromptIds.size})
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PromptSelector;
